import React, { useEffect, useMemo, useState } from "react";
import LeftRail from "../components/layout/LeftRail";
import TopBar from "../components/layout/TopBar";
import MobileMenu from "../components/layout/MobileMenu";
import HeroBanners from "../components/layout/HeroBanners";
import LiveDropsStrip from "../components/cases/LiveDropsStrip";
import ChatPanel from "../components/chat/ChatPanel";
import VipModal from "../components/modals/VipModal";
import AffiliatesModal from "../components/modals/AffiliatesModal";
import DailyCaseModal from "../components/modals/DailyCaseModal";
import CaseCard from "../components/cases/CaseCard";
import CaseDetailModal from "../components/cases/CaseDetailModal";
import InventoryGrid from "../components/cases/InventoryGrid";
import BattleSection from "../components/cases/BattleSection";
import Footer from "../components/Footer";
import { CASES } from "../data/casesData";
import { Package } from "lucide-react";

const FILTERS = [
  { id: "featured", label: "Featured" },
  { id: "new", label: "Low to High" },
  { id: "top", label: "High to Low" },
];

export default function CasesPage() {
  const [tab, setTab] = useState("cases");
  const [filter, setFilter] = useState("featured");
  const [active, setActive] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [vipOpen, setVipOpen] = useState(false);
  const [affOpen, setAffOpen] = useState(false);
  const [dailyOpen, setDailyOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Force the dark palette so shadcn + existing dark: variants render correctly.
  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  const cases = useMemo(() => {
    const c = [...CASES];
    if (filter === "new") c.sort((a, b) => a.price - b.price);           // Low to High
    else if (filter === "top") c.sort((a, b) => b.price - a.price);      // High to Low
    else c.sort((a, b) => b.opens - a.opens);                            // Featured = most popular
    return c;
  }, [filter]);

  return (
    <div className="uc-glow relative min-h-screen overflow-x-hidden bg-[var(--uc-app)] font-sans text-[var(--uc-text)]">
      <LeftRail
        tab={tab}
        setTab={setTab}
        onDailyCase={() => setDailyOpen(true)}
        onVip={() => setVipOpen(true)}
        onAffiliates={() => setAffOpen(true)}
        onToggleChat={() => setChatOpen((o) => !o)}
        onProvablyFair={() => setActive(CASES[0])}
      />

      <div className={`relative z-10 ml-0 transition-[margin] duration-300 lg:ml-[72px] ${chatOpen ? "lg:mr-[340px]" : ""}`}>
        <TopBar
          tab={tab}
          setTab={setTab}
          onVip={() => setVipOpen(true)}
          onAffiliates={() => setAffOpen(true)}
          onToggleChat={() => setChatOpen((o) => !o)}
          onOpenMenu={() => setMenuOpen(true)}
        />

        <main className="mx-auto max-w-[1600px] space-y-6 p-4 md:p-6 lg:p-8">
          <LiveDropsStrip />

          {tab === "cases" && (
            <>
              <HeroBanners onRefer={() => setAffOpen(true)} />

              <section>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-white">
                    <Package className="h-5 w-5 text-[var(--uc-primary)]" /> Featured Boxes
                  </h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center rounded-xl border border-[var(--uc-border)] bg-[var(--uc-rail)] p-1">
                      {FILTERS.map((f) => (
                        <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)}
                          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f.id ? "bg-[var(--uc-primary)] text-white" : "text-[var(--uc-muted)] hover:text-white"}`}>
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div data-testid="featured-boxes-grid" className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                  {cases.map((c) => <CaseCard key={c.id} c={c} onOpen={setActive} onOdds={setActive} />)}
                </div>
              </section>
            </>
          )}

          {tab === "battles" && <BattleSection />}
          {tab === "inventory" && <InventoryGrid />}
        </main>

        <Footer />
      </div>

      <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />

      <CaseDetailModal caseObj={active} list={cases} onSelect={setActive} open={!!active} onClose={() => setActive(null)} />
      <VipModal open={vipOpen} onOpenChange={setVipOpen} />
      <AffiliatesModal open={affOpen} onOpenChange={setAffOpen} />
      <DailyCaseModal open={dailyOpen} onOpenChange={setDailyOpen} />
      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        tab={tab}
        setTab={setTab}
        onDailyCase={() => setDailyOpen(true)}
        onVip={() => setVipOpen(true)}
        onAffiliates={() => setAffOpen(true)}
        onToggleChat={() => setChatOpen((o) => !o)}
        onProvablyFair={() => setActive(CASES[0])}
      />
    </div>
  );
}
